/**
 * ReEngagementTab - Re-engagement campaign management
 */
import { Settings, UserMinus, Play, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import { DISCOUNT_OPTIONS, INACTIVE_DAYS_OPTIONS, MAX_SENDS_OPTIONS } from './automatedUtils';

export default function ReEngagementTab({
  settings,
  eligibleContacts,
  sendingReengagement,
  onUpdateSettings,
  onSendReengagementOffer,
  onRefresh
}) {
  const runNow = async () => {
    try {
      await api.post('/automated-campaigns/reengagement/run-now');
      toast.success('Re-engagement campaign triggered');
      setTimeout(onRefresh, 2000);
    } catch (err) {
      toast.error('Failed to trigger campaign');
    }
  };

  if (!settings) return null;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Settings */}
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-foreground flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Re-engagement Settings
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={runNow}
            data-testid="reengagement-run-now"
          >
            <Play className="w-3 h-3 mr-1" />
            Run Now
          </Button>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Enable Re-engagement</p>
              <p className="text-xs text-muted-foreground">Win back inactive customers</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => onUpdateSettings({ enabled: checked })}
              data-testid="reengagement-enabled-switch"
            />
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Inactive Days Threshold</Label>
            <select
              value={settings.inactive_days}
              onChange={(e) => onUpdateSettings({ inactive_days: parseInt(e.target.value) })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {INACTIVE_DAYS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Discount Percentage (Max 15%)</Label>
            <select
              value={settings.discount_percent}
              onChange={(e) => onUpdateSettings({ discount_percent: parseInt(e.target.value) })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {DISCOUNT_OPTIONS.map(v => (
                <option key={v} value={v}>{v}% off</option>
              ))}
            </select>
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Max Sends per Contact</Label>
            <select
              value={settings.max_sends_per_contact}
              onChange={(e) => onUpdateSettings({ max_sends_per_contact: parseInt(e.target.value) })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {MAX_SENDS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Eligible Contacts */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
          <UserMinus className="w-5 h-5 text-orange-500" />
          Inactive Customers ({eligibleContacts.length})
        </h3>
        
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {eligibleContacts.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No inactive customers to re-engage</p>
          ) : (
            eligibleContacts.slice(0, 15).map((contact) => (
              <div key={contact.contact_id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="font-medium text-sm">{contact.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {contact.days_inactive} days inactive • ${contact.total_spent} lifetime
                    {contact.sends_received > 0 && ` • ${contact.sends_received} sent`}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSendReengagementOffer(contact.contact_id)}
                  disabled={sendingReengagement === contact.contact_id}
                  data-testid={`send-reengagement-${contact.contact_id}`}
                >
                  {sendingReengagement === contact.contact_id ? 'Sending...' : (
                    <>
                      <Send className="w-3 h-3 mr-1" /> Win Back
                    </>
                  )}
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
